// REACT
import { ReactNode } from "react";
import { useHistory } from "react-router-dom";

// MATERIAL UI
import { Skeleton, Stack, Typography } from "@mui/material";

// COMPONENTS
import ContainedButton from "src/components/common/ContainedButton";

// REDUX
import api from "src/store/features/api";

// STYLES
import { Card, NAVY, StatusPill, TEXT_SECONDARY } from "./styles";

interface TaxAccountGateProps {
  children: ReactNode;
}

// 404 = no TaxBit account for this customer; anything else = TaxBit unreachable.
const TaxAccountGate = ({ children }: TaxAccountGateProps) => {
  const history = useHistory();
  const accountQuery = api.useGetTaxAccountQuery(undefined, { refetchOnMountOrArgChange: true });

  if (accountQuery.isLoading) {
    return <Skeleton variant="rounded" height={220} />;
  }

  if (accountQuery.isError || !accountQuery.data) {
    const notFound = (accountQuery.error as { status?: number } | undefined)?.status === 404;
    return (
      <Card sx={{ p: 3.25, display: "flex", flexDirection: "column", gap: 1.75, maxWidth: 760 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
          <Typography sx={{ fontSize: 17, fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
            {notFound ? "Your tax account isn't set up yet" : "Tax account unavailable"}
          </Typography>
          <StatusPill tone="warning">Action required</StatusPill>
        </Stack>
        <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.55 }}>
          {notFound
            ? "Transaction review and tax settings become available once your tax account is set up. Head back to the Tax Center and complete your tax documentation first."
            : "We couldn't load your tax account right now. Please try again shortly."}
        </Typography>
        <Stack direction="row" sx={{ pt: 0.5 }}>
          <ContainedButton
            color="salmon"
            width="170px"
            height="38px"
            onClick={() => {
              void accountQuery.refetch();
              if (notFound) history.push("/tax-center");
            }}
          >
            {notFound ? "Back to Tax Center" : "Try again"}
          </ContainedButton>
        </Stack>
      </Card>
    );
  }

  return <>{children}</>;
};

export default TaxAccountGate;
